import { useMemo } from "react";
import { usePortalConfig } from "./portalConfig";

/**
 * The one place the portal spells the name of the document a customer signs.
 *
 * The *Customer documents* setting (`quotation_kind`) is portal-wide, and the
 * kind is frozen on each row (`quotations.kind`). So two rules, decided
 * 2026-11-03 (knowledge/tasks/…-a-statement-of-work-portal-never-says-quotation):
 *
 * - **An aggregate surface** — a heading, chip, filter, menu, count, export
 *   panel, settings tab — says the word for the CURRENT setting:
 *   `useDocumentWord()`.
 * - **One document** — a row, a preview, the signing page, a dialog about one
 *   order — says the word for ITS OWN `kind`: `documentWord(q.kind, …)`.
 *
 * While the setting is `quotation` every call returns exactly what the
 * screens spelled before this helper existed — lower case unless asked,
 * "quotations" in the plural — so that setting reads byte for byte as it did.
 * "Statement of Work" is a name and is always capitalised.
 */
export const STATEMENT_OF_WORK = "statement_of_work";

export function isStatementOfWork(kind) {
  return kind === STATEMENT_OF_WORK;
}

export function documentWord(kind, { plural = false, capital = false } = {}) {
  if (isStatementOfWork(kind)) return plural ? "Statements of Work" : "Statement of Work";
  const word = plural ? "quotations" : "quotation";
  return capital ? word[0].toUpperCase() + word.slice(1) : word;
}

/**
 * The word for the current setting, as a function taking the same options as
 * `documentWord`; `.kind` is the setting itself, for a surface that has to
 * choose a whole sentence rather than one word. Falls back to `quotation`,
 * the server's default, until /auth/config has answered.
 */
export function useDocumentWord() {
  const kind = usePortalConfig()?.quotation_kind || "quotation";
  return useMemo(() => {
    const word = opts => documentWord(kind, opts);
    word.kind = kind;
    word.sow = isStatementOfWork(kind);
    return word;
  }, [kind]);
}
